import { Github, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AiPortfolio } from '../../../types';
import Markdown from '../../ui/markdown';
import PipelineViewer from '../../ui/pipeline-viewer';
import CopyUrlButton from '../../ui/copy-url-button';

type Tab = 'article' | 'pipelines';

const AiPortfolioDetail = ({ project }: { project: AiPortfolio }) => {
  const hasArticle = !!project.markdown;
  const hasPipelines = !!(project.pipelines && project.pipelines.length);
  const showTabs = hasArticle && hasPipelines;

  const [activeTab, setActiveTab] = useState<Tab>(
    hasArticle ? 'article' : 'pipelines',
  );

  // Reset the active tab when a different project is opened in the sheet.
  useEffect(() => {
    setActiveTab(project.markdown ? 'article' : 'pipelines');
  }, [project.slug, project.markdown]);

  const currentTab: Tab = showTabs
    ? activeTab
    : hasArticle
      ? 'article'
      : 'pipelines';

  const tabs: { key: Tab; label: string }[] = [
    ...(hasArticle ? [{ key: 'article' as const, label: 'Article' }] : []),
    ...(hasPipelines
      ? [{ key: 'pipelines' as const, label: 'Pipelines' }]
      : []),
  ];

  return (
    <div className="pt-2 pb-16 flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 sm:items-center">
        {project.image && (
          <img
            src={project.image}
            alt={project.title}
            className="w-20 h-20 rounded-2xl flex-none"
          />
        )}
        <div className="flex flex-col gap-2">
          {project.status && (
            <span className="flex items-center gap-1.5 w-fit text-xs font-medium text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              {project.status}
            </span>
          )}
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900">
            {project.title}
          </h2>
          {project.tagline && (
            <p className="text-base text-blackSecondary">{project.tagline}</p>
          )}
        </div>
      </div>

      {/* Tags */}
      {project.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {project.tags.map((tag, index) => (
            <span
              key={index}
              className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Links */}
      <div className="flex flex-wrap gap-3">
        {project.repoUrl && (
          <a
            href={project.repoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-full px-4 py-2 hover:bg-gray-100 brand-transition"
          >
            <Github className="h-4 w-4" />
            View code
          </a>
        )}
        {project.liveUrl && (
          <a
            href={project.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm font-medium text-white bg-brand rounded-full px-4 py-2 hover:opacity-90 brand-transition"
          >
            <ExternalLink className="h-4 w-4" />
            Live demo
          </a>
        )}
        <CopyUrlButton />
      </div>

      {/* Tabs — switch the content below between the write-up and the pipelines */}
      {showTabs && (
        <div className="flex gap-6 border-b border-gray-100">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative pb-3 text-sm font-medium brand-transition ${
                currentTab === tab.key
                  ? 'text-brand'
                  : 'text-blackSecondary opacity-50 hover:opacity-100 hover:text-brand'
              }`}
            >
              {tab.label}
              {currentTab === tab.key && (
                <div className="absolute -bottom-px left-0 h-[1.5px] w-full bg-brand rounded-full" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Case study (rendered from the project's Markdown file) */}
      {currentTab === 'article' && project.markdown && (
        <div className="pt-2">
          <Markdown src={project.markdown} stripFirstH1 />
        </div>
      )}

      {/* Pipelines (n8n workflows rendered via <n8n-demo>) */}
      {currentTab === 'pipelines' && project.pipelines && (
        <div className="pt-2 flex flex-col gap-8">
          {project.pipelines.map((pipeline) => (
            <PipelineViewer
              key={pipeline.path}
              src={pipeline.path}
              name={pipeline.name}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AiPortfolioDetail;
